import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Tag,
  Space,
  Alert,
  Progress,
  message,
  Spin,
  Empty,
  Modal,
  Input,
  Steps,
  Switch,
  Tabs,
  Badge,
  Dropdown,
  type MenuProps,
} from "antd";
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  MinusOutlined,
  TruckOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  ScanOutlined,
  MoreOutlined,
  InboxOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import dayjs from "dayjs";
import { ShippingOptions } from "../components/sendcloud/ShippingOptions";
import { SendcloudShipments } from "../components/sendcloud/SendcloudShipments";
import {
  pdf,
  Document,
  Page,
  Text as PDFText,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import "../styles/ContainerUnloading.css";

// Touch optimization utilities
const hapticFeedback = () => {
  // Trigger haptic feedback on supported devices
  if ("vibrate" in navigator) {
    navigator.vibrate(10); // Light vibration
  }
};

const preventZoom = (e: TouchEvent) => {
  // Prevent double-tap zoom on mobile
  if (e.touches.length > 1) {
    e.preventDefault();
  }
};

// Add touch event listeners for mobile optimization
if (typeof document !== "undefined") {
  document.addEventListener("touchstart", preventZoom, { passive: false });
}

const { Title, Text } = Typography;

const { Step } = Steps;

interface Product {
  id: number;
  booking_id: number;
  booking_reference: string;
  company_name: string;
  name: string;
  ean: string | null;
  estimated_cartons: number;
  actual_cartons: number | null;
  pallets: number | null; // For storage bookings - pallets per product
  completed: boolean;
  cbm: number | null;
  gross_weight: number | null;
  pieces: number | null;
  // Company product dimensions (if available)
  company_product_dimensions?: {
    length: number;
    width: number;
    height: number;
  } | null;
}

interface BookingInfo {
  id: number;
  booking_reference: string;
  company_name: string;
  products: Product[];
  unloading_status: "pending" | "counting" | "delivery_ready" | "completed";
  total_actual_cartons: number | null;
  total_actual_pallets: number | null;
  palletizing_notes: string | null;
  has_delivery_address: boolean;
  delivery_type: "storage" | "delivery";
  delivery_info?: {
    id: number;
    street_name?: string;
    house_number?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    shipment_status?: string;
  };
}

interface ContainerInfo {
  id: number;
  reference: string;
  status: string;
  unloading_status: string | null;
  unloading_started_at: string | null;
  unloading_completed_at: string | null;
}

const ContainerUnloadingPage: React.FC = () => {
  const { containerId } = useParams<{ containerId: string }>();
  const navigate = useNavigate();

  const [container, setContainer] = useState<ContainerInfo | null>(null);
  const [bookings, setBookings] = useState<BookingInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [startingUnload, setStartingUnload] = useState(false);

  // Modal states
  const [palletLabelModalVisible, setPalletLabelModalVisible] = useState(false);
  const [deliveryModalVisible, setDeliveryModalVisible] = useState(false);
  const [containerResetModalVisible, setContainerResetModalVisible] =
    useState(false);
  const [shortcutsModalVisible, setShortcutsModalVisible] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingInfo | null>(
    null
  );

  const [refreshKey, setRefreshKey] = useState(0);

  // Barcode scanner states
  const [highlightedProductId, setHighlightedProductId] = useState<
    number | null
  >(null);
  const productRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [autoScanEnabled, setAutoScanEnabled] = useState(false);
  const barcodeBuffer = useRef("");
  const barcodeTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastScannedEAN = useRef<string>("");
  const lastScanTime = useRef<number>(0);

  // UI state for optimization - persist active tab in localStorage
  const [activeTab, setActiveTabState] = useState<string>(() => {
    return (
      localStorage.getItem(`container-${containerId}-active-tab`) || "shipping"
    );
  });

  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
    localStorage.setItem(`container-${containerId}-active-tab`, tab);
  };

  const [expandedBookings, setExpandedBookings] = useState<Set<number>>(
    new Set()
  );

  // Search functionality
  const [searchQuery, setSearchQuery] = useState<string>("");

  // LOCAL STATE FOR PENDING CHANGES (not saved to database until "Finish")
  const [pendingCartonUpdates, setPendingCartonUpdates] = useState<
    Record<number, number>
  >({});
  const [pendingPalletUpdates, setPendingPalletUpdates] = useState<
    Record<number, number>
  >({});
  const [pendingBookingPallets, setPendingBookingPallets] = useState<
    Record<number, number>
  >({});
  const [finishingBookings, setFinishingBookings] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    if (!containerId) return;
    fetchContainerData();
  }, [containerId]);

  const fetchContainerData = async () => {
    try {
      setLoading(true);

      // Fetch container info
      const { data: containerData, error: containerError } = await supabase
        .from("containers")
        .select(
          "id, reference, status, unloading_status, unloading_started_at, unloading_completed_at"
        )
        .eq("id", containerId)
        .single();

      if (containerError) throw containerError;
      setContainer(containerData);

      // Fetch bookings with their products and delivery info
      const { data: bookingsData, error: bookingsError } = await supabase
        .from("bookings")
        .select(
          `
          id,
          unloading_status,
          total_actual_cartons,
          total_actual_pallets,
          unloading_notes,
          company_info:company(name),
          transport_type_info:booking_transport_types(abbreviation),
          booking_delivery_info:booking_delivery(
            id,
            street_name,
            house_number,
            city,
            postal_code,
            country,
            shipment_status
          )
        `
        )
        .eq("container_id", containerId);

      if (bookingsError) throw bookingsError;

      // Transform bookings data with proper type casting
      const transformedBookings = await Promise.all(
        (bookingsData || []).map(async (bookingData: any) => {
          // Fetch products for this booking
          const { data: productsData, error: productsError } = await supabase
            .from("booking_product")
            .select(
              `
              id, 
              name, 
              ean, 
              cartons, 
              actual_cartons,
              pallets,
              cbm,
              gross_weight,
              pieces
            `
            )
            .eq("booking_id", bookingData.id);

          if (productsError) throw productsError;

          // Transform products
          const products: Product[] = (productsData || []).map(
            (product: any) => ({
              id: product.id,
              booking_id: bookingData.id,
              booking_reference: `EFF${bookingData.id}${
                bookingData.transport_type_info?.abbreviation || ""
              }`,
              company_name: bookingData.company_info?.name || "Unknown",
              name: product.name,
              ean: product.ean,
              estimated_cartons: product.cartons || 0,
              actual_cartons: product.actual_cartons,
              pallets: product.pallets || null,
              completed: product.actual_cartons !== null,
              cbm: product.cbm,
              gross_weight: product.gross_weight,
              pieces: product.pieces,
              // Company product dimensions not available yet - to be implemented later
              company_product_dimensions: null,
            })
          );

          // Determine delivery info
          const deliveryInfo = Array.isArray(bookingData.booking_delivery_info)
            ? bookingData.booking_delivery_info[0]
            : bookingData.booking_delivery_info;

          const hasDeliveryAddress = deliveryInfo && deliveryInfo.street_name;

          // Determine booking status
          const allProductsCounted = products.every((p) => p.completed);

          let status: BookingInfo["unloading_status"] = "pending";
          if (bookingData.unloading_status) {
            status = bookingData.unloading_status;
          } else if (allProductsCounted && products.length > 0) {
            // Skip palletizing, go directly to delivery_ready or completed
            status = hasDeliveryAddress ? "delivery_ready" : "completed";
          } else if (products.some((p) => p.completed)) {
            status = "counting";
          }

          return {
            id: bookingData.id,
            booking_reference: `EFF${bookingData.id}${
              bookingData.transport_type_info?.abbreviation || ""
            }`,
            company_name: bookingData.company_info?.name || "Unknown",
            products,
            unloading_status: status,
            total_actual_cartons: bookingData.total_actual_cartons,
            total_actual_pallets: bookingData.total_actual_pallets,
            palletizing_notes: bookingData.unloading_notes,
            has_delivery_address: Boolean(hasDeliveryAddress),
            delivery_type: hasDeliveryAddress
              ? ("delivery" as const)
              : ("storage" as const),
            delivery_info: deliveryInfo
              ? {
                  id: deliveryInfo.id,
                  street_name: deliveryInfo.street_name,
                  house_number: deliveryInfo.house_number,
                  city: deliveryInfo.city,
                  postal_code: deliveryInfo.postal_code,
                  country: deliveryInfo.country,
                  shipment_status: deliveryInfo.shipment_status,
                }
              : undefined,
          };
        })
      );

      setBookings(transformedBookings);
    } catch (error) {
      console.error("Error fetching container data:", error);
      message.error("Failed to load container data");
    } finally {
      setLoading(false);
    }
  };

  // Search for product by EAN from barcode scanner or search input
  const searchProductByEAN = (
    scannedCode: string,
    fromScanner: boolean = false
  ) => {
    console.log(
      `🚀 searchProductByEAN called with: code="${scannedCode}", fromScanner=${fromScanner}`
    );

    if (!scannedCode.trim()) {
      setHighlightedProductId(null);
      return;
    }

    const currentTime = Date.now();
    const trimmedCode = scannedCode.trim();

    // Prevent duplicate scans within 500ms of the same EAN (only for scanner input)
    if (
      fromScanner &&
      lastScannedEAN.current === trimmedCode &&
      currentTime - lastScanTime.current < 500
    ) {
      console.log("🚫 Duplicate scan prevented");
      return;
    }

    // Update last scan tracking
    lastScannedEAN.current = trimmedCode;
    lastScanTime.current = currentTime;

    // Find product with matching EAN
    let foundProduct: Product | null = null;
    let foundBooking: BookingInfo | null = null;
    let isPartialMatch = false;

    // First try exact match
    for (const booking of bookings) {
      for (const product of booking.products) {
        if (product.ean && product.ean.toString() === trimmedCode) {
          foundProduct = product;
          foundBooking = booking;
          break;
        }
      }
      if (foundProduct) break;
    }

    // If no exact match and code is 8 digits, try partial match (scanner truncation workaround)
    if (
      !foundProduct &&
      trimmedCode.length === 8 &&
      /^\d{8}$/.test(trimmedCode)
    ) {
      console.log(
        `🔍 No exact match for 8-digit code "${trimmedCode}", trying partial match...`
      );

      const partialMatches: { product: Product; booking: BookingInfo }[] = [];

      for (const booking of bookings) {
        for (const product of booking.products) {
          if (product.ean && product.ean.toString().startsWith(trimmedCode)) {
            partialMatches.push({ product, booking });
          }
        }
      }

      if (partialMatches.length === 1) {
        // Single match found - use it
        foundProduct = partialMatches[0].product;
        foundBooking = partialMatches[0].booking;
        isPartialMatch = true;
        console.log(
          `✅ Found single partial match: EAN ${foundProduct.ean} starts with ${trimmedCode}`
        );
      } else if (partialMatches.length > 1) {
        // Multiple matches - show warning but use first one
        foundProduct = partialMatches[0].product;
        foundBooking = partialMatches[0].booking;
        isPartialMatch = true;
        console.warn(
          `⚠️ Multiple partial matches found for "${trimmedCode}":`,
          partialMatches.map((m) => m.product.ean).join(", ")
        );
      }
    }

    if (foundProduct && foundBooking) {
      // Auto-expand the booking to show the product
      setExpandedBookings((prev) => new Set([...prev, foundBooking.id]));

      // Highlight the product with enhanced styling for Zebra scanner visibility
      setHighlightedProductId(foundProduct.id);

      // Add haptic feedback for mobile devices
      hapticFeedback();

      // Small delay to ensure DOM is updated before scrolling
      setTimeout(() => {
        const productElement = productRefs.current[foundProduct.id];
        if (productElement) {
          productElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      // Determine if we should auto-increment
      // Only auto-increment if:
      // 1. It's from the scanner (not search input)
      // 2. Auto-scan is enabled
      // 3. Unloading is in progress
      // 4. Product is not completed
      const shouldAutoIncrement =
        fromScanner &&
        autoScanEnabled &&
        isUnloadingInProgress &&
        !foundProduct.completed;

      // Determine destination for clear messaging
      const destination = foundBooking.has_delivery_address
        ? "DELIVERY 🚚"
        : "STORAGE 📦";
      const destinationColor = foundBooking.has_delivery_address
        ? "#1890ff"
        : "#fa8c16";

      if (shouldAutoIncrement) {
        const currentCartons = foundProduct.actual_cartons || 0;
        const newCartons = currentCartons + 1;
        console.log(
          `✅ Auto incrementing from ${currentCartons} to ${newCartons}`
        );
        updateProductCartons(foundProduct.id, newCartons);

        // Show prominent destination message
        message.success({
          content: (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  marginBottom: "4px",
                }}
              >
                +1 CARTON ADDED{isPartialMatch ? " (Partial EAN)" : ""}
              </div>
              <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                {foundProduct.name} in {foundBooking.booking_reference}
              </div>
              {isPartialMatch && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#fa8c16",
                    marginBottom: "4px",
                  }}
                >
                  Scanned: {trimmedCode} → Matched: {foundProduct.ean}
                </div>
              )}
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: destinationColor,
                  backgroundColor: foundBooking.has_delivery_address
                    ? "#f0f8ff"
                    : "#fff7e6",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: `2px solid ${destinationColor}`,
                }}
              >
                → {destination}
              </div>
              <div
                style={{ fontSize: "12px", marginTop: "4px", color: "#666" }}
              >
                ({newCartons}/{foundProduct.estimated_cartons} cartons)
              </div>
            </div>
          ),
          duration: 4,
          style: { width: "320px" },
        });
      } else {
        // Just highlight/open the product without incrementing
        console.log(
          `📍 Product highlighted: fromScanner=${fromScanner}, autoScanEnabled=${autoScanEnabled}, isUnloadingInProgress=${isUnloadingInProgress}, completed=${foundProduct.completed}`
        );

        // Show prominent destination message for search
        message.info({
          content: (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  marginBottom: "4px",
                }}
              >
                PRODUCT FOUND
              </div>
              <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                {foundProduct.name} in {foundBooking.booking_reference}
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: destinationColor,
                  backgroundColor: foundBooking.has_delivery_address
                    ? "#f0f8ff"
                    : "#fff7e6",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: `2px solid ${destinationColor}`,
                }}
              >
                → {destination}
              </div>
              <div
                style={{ fontSize: "12px", marginTop: "4px", color: "#666" }}
              >
                ({foundProduct.actual_cartons || 0}/
                {foundProduct.estimated_cartons} cartons)
              </div>
            </div>
          ),
          duration: 3,
          style: { width: "300px" },
        });
      }

      // Clear highlight after 4 seconds (longer for better visibility)
      setTimeout(() => {
        setHighlightedProductId(null);
      }, 4000);
    } else {
      message.warning(`No product found with EAN: ${scannedCode}`, 3);
      setHighlightedProductId(null);
    }
  };

  const startUnloading = async () => {
    try {
      setStartingUnload(true);

      const { error: containerError } = await supabase
        .from("containers")
        .update({
          unloading_status: "in_progress",
          unloading_started_at: new Date().toISOString(),
        })
        .eq("id", containerId);

      if (containerError) throw containerError;

      // Update local container state instead of full refresh
      setContainer((prev) =>
        prev
          ? {
              ...prev,
              unloading_status: "in_progress",
              unloading_started_at: new Date().toISOString(),
            }
          : null
      );

      message.success("Container unloading started!");
    } catch (error) {
      console.error("Error starting unloading:", error);
      message.error("Failed to start unloading");
    } finally {
      setStartingUnload(false);
    }
  };

  const resetContainer = async () => {
    try {
      setUpdating("container-reset");

      // Reset container status
      const { error: containerError } = await supabase
        .from("containers")
        .update({
          unloading_status: null,
          unloading_started_at: null,
          unloading_completed_at: null,
        })
        .eq("id", containerId);

      if (containerError) throw containerError;

      // Reset all bookings in this container
      const { error: bookingError } = await supabase
        .from("bookings")
        .update({
          unloading_status: null,
          total_actual_cartons: null,
          total_actual_pallets: null,
          unloading_notes: null,
        })
        .eq("container_id", containerId);

      if (bookingError) throw bookingError;

      // Reset all product counts in this container
      const bookingIds = bookings.map((b) => b.id);
      if (bookingIds.length > 0) {
        const { error: productError } = await supabase
          .from("booking_product")
          .update({ actual_cartons: null })
          .in("booking_id", bookingIds);

        if (productError) throw productError;
      }

      // Update local state to reflect reset
      setContainer((prev) =>
        prev
          ? {
              ...prev,
              unloading_status: null,
              unloading_started_at: null,
              unloading_completed_at: null,
            }
          : null
      );

      setBookings((prevBookings) =>
        prevBookings.map((booking) => ({
          ...booking,
          unloading_status: "pending",
          total_actual_cartons: null,
          total_actual_pallets: null,
          palletizing_notes: null,
          products: booking.products.map((product) => ({
            ...product,
            actual_cartons: null,
            completed: false,
          })),
        }))
      );

      message.success("Container completely reset!");
      setContainerResetModalVisible(false);
    } catch (error) {
      console.error("Error resetting container:", error);
      message.error("Failed to reset container");
    } finally {
      setUpdating(null);
    }
  };

  // Update product cartons in LOCAL STATE only (not saved to database until "Finish")
  const updateProductCartons = (productId: number, cartons: number) => {
    // Store the pending update
    setPendingCartonUpdates((prev) => ({
      ...prev,
      [productId]: cartons,
    }));

    // Update local state for immediate UI feedback
    setBookings((prevBookings) =>
      prevBookings.map((booking) => ({
        ...booking,
        products: booking.products.map((product) =>
          product.id === productId
            ? {
                ...product,
                actual_cartons: cartons,
                completed:
                  cartons !== null && cartons === product.estimated_cartons,
              }
            : product
        ),
        // Don't auto-progress status until "Finish" is clicked
        unloading_status: (() => {
          const updatedProducts = booking.products.map((product) =>
            product.id === productId
              ? {
                  ...product,
                  actual_cartons: cartons,
                  completed:
                    cartons !== null && cartons === product.estimated_cartons,
                }
              : product
          );

          if (booking.unloading_status === "completed") return "completed";
          if (updatedProducts.some((p) => p.completed)) return "counting";
          return "pending";
        })(),
      }))
    );

    hapticFeedback();
  };

  const quickAddCartons = (productId: number, amount: number) => {
    const product = bookings
      .flatMap((b) => b.products)
      .find((p) => p.id === productId);

    if (!product) return;

    const currentCartons = product.actual_cartons || 0;
    const newCartons = Math.max(0, currentCartons + amount);
    updateProductCartons(productId, newCartons);
  };

  const setAllBoxesCounted = (productId: number) => {
    const product = bookings
      .flatMap((b) => b.products)
      .find((p) => p.id === productId);

    if (!product) return;

    updateProductCartons(productId, product.estimated_cartons);
  };

  const resetProductCount = (productId: number) => {
    updateProductCartons(productId, 0);
  };

  // Update product pallets in LOCAL STATE only (not saved to database until "Finish")
  const updateProductPallets = (productId: number, pallets: number) => {
    // Store the pending update
    setPendingPalletUpdates((prev) => ({
      ...prev,
      [productId]: pallets,
    }));

    // Update local state for immediate UI feedback
    setBookings((prevBookings) =>
      prevBookings.map((booking) => ({
        ...booking,
        products: booking.products.map((product) =>
          product.id === productId ? { ...product, pallets: pallets } : product
        ),
      }))
    );

    hapticFeedback();
  };

  // Quick add pallets for products
  const quickAddProductPallets = (productId: number, amount: number) => {
    const product = bookings
      .flatMap((b) => b.products)
      .find((p) => p.id === productId);
    if (product) {
      const currentPallets = product.pallets || 0;
      const newPallets = Math.max(0, currentPallets + amount);
      updateProductPallets(productId, newPallets);
    }
  };

  // Update booking-level pallets in LOCAL STATE only (not saved to database until "Finish")
  const updateBookingPallets = (bookingId: number, pallets: number) => {
    // Store the pending update
    setPendingBookingPallets((prev) => ({
      ...prev,
      [bookingId]: pallets,
    }));

    // Update local state for immediate UI feedback
    setBookings((prevBookings) =>
      prevBookings.map((booking) => {
        if (booking.id !== bookingId) return booking;
        return {
          ...booking,
          total_actual_pallets: pallets,
        };
      })
    );

    hapticFeedback();
  };

  // Quick add booking pallets
  const quickAddBookingPallets = (bookingId: number, amount: number) => {
    const booking = bookings.find((b) => b.id === bookingId);
    if (booking) {
      const currentPallets = booking.total_actual_pallets || 0;
      const newPallets = Math.max(0, currentPallets + amount);
      updateBookingPallets(bookingId, newPallets);
    }
  };

  // Check if booking is ready to finish (all cartons counted)
  const isBookingReadyToFinish = (booking: BookingInfo): boolean => {
    return booking.products.every((p) => p.completed);
  };

  // Check if booking has pending changes
  const hasBookingPendingChanges = (booking: BookingInfo): boolean => {
    // Check if any products have pending carton or pallet updates
    const hasProductChanges = booking.products.some(
      (p) =>
        pendingCartonUpdates[p.id] !== undefined ||
        pendingPalletUpdates[p.id] !== undefined
    );

    // Check if booking has pending pallet updates
    const hasBookingPalletChanges =
      pendingBookingPallets[booking.id] !== undefined;

    return hasProductChanges || hasBookingPalletChanges;
  };

  // Finish booking - save all pending changes to database
  const finishBooking = async (booking: BookingInfo) => {
    if (!isBookingReadyToFinish(booking)) {
      message.error("Not all products have been counted yet");
      return;
    }

    try {
      setFinishingBookings((prev) => new Set([...prev, booking.id]));

      // Save all product carton updates
      const productUpdates = booking.products.map(async (product) => {
        const pendingCartons = pendingCartonUpdates[product.id];
        if (pendingCartons !== undefined) {
          const { error } = await supabase
            .from("booking_product")
            .update({ actual_cartons: pendingCartons })
            .eq("id", product.id);
          if (error) throw error;
        }
      });

      // Save all product pallet updates (for storage bookings)
      const palletUpdates = booking.products.map(async (product) => {
        const pendingPallets = pendingPalletUpdates[product.id];
        if (pendingPallets !== undefined) {
          const { error } = await supabase
            .from("booking_product")
            .update({ pallets: pendingPallets })
            .eq("id", product.id);
          if (error) throw error;
        }
      });

      await Promise.all([...productUpdates, ...palletUpdates]);

      // Calculate total actual cartons
      const totalActualCartons = booking.products.reduce((sum, product) => {
        const cartons =
          pendingCartonUpdates[product.id] ?? product.actual_cartons ?? 0;
        return sum + cartons;
      }, 0);

      // For delivery bookings, save booking-level data
      const bookingPallets =
        pendingBookingPallets[booking.id] ?? booking.total_actual_pallets;

      // Determine final status - skip palletizing step
      const finalStatus = booking.has_delivery_address
        ? "delivery_ready"
        : "completed";

      // Update booking with totals and status
      const { error: bookingError } = await supabase
        .from("bookings")
        .update({
          total_actual_cartons: totalActualCartons,
          total_actual_pallets: bookingPallets,
          unloading_status: finalStatus,
        })
        .eq("id", booking.id);

      if (bookingError) throw bookingError;

      // Auto-create delivery info if needed
      if (finalStatus === "delivery_ready") {
        await autoCreateDeliveryInfo(booking.id);
      }

      // Clear pending updates for this booking
      setPendingCartonUpdates((prev) => {
        const newState = { ...prev };
        booking.products.forEach((p) => delete newState[p.id]);
        return newState;
      });

      setPendingPalletUpdates((prev) => {
        const newState = { ...prev };
        booking.products.forEach((p) => delete newState[p.id]);
        return newState;
      });

      setPendingBookingPallets((prev) => {
        const newState = { ...prev };
        delete newState[booking.id];
        return newState;
      });

      // Refresh data to get latest state
      await fetchContainerData();

      message.success(`Booking ${booking.booking_reference} completed!`);
    } catch (error) {
      console.error("Error finishing booking:", error);
      message.error("Failed to save booking data");
    } finally {
      setFinishingBookings((prev) => {
        const newSet = new Set(prev);
        newSet.delete(booking.id);
        return newSet;
      });
    }
  };

  // Auto-create delivery info when booking becomes delivery_ready
  const autoCreateDeliveryInfo = async (bookingId: number) => {
    try {
      const booking = bookings.find((b) => b.id === bookingId);
      if (!booking || !booking.has_delivery_address) return;

      // Check if delivery info already exists
      if (booking.delivery_info?.id) {
        // Update existing delivery info with actual counts
        const { error } = await supabase
          .from("booking_delivery")
          .update({
            delivery_cartons: booking.total_actual_cartons,
            delivery_pallets: booking.total_actual_pallets,
            shipment_status: "planned",
          })
          .eq("id", booking.delivery_info.id);

        if (error) throw error;
        console.log("Updated existing delivery info with actual counts");
      } else {
        console.log(
          "Delivery address exists but no delivery info record found - this shouldn't happen"
        );
      }
    } catch (error) {
      console.error("Error auto-creating delivery info:", error);
    }
  };

  // Simple shipment creation - replaces the complex delivery modal
  const handleSimpleShipmentCreation = (booking: BookingInfo) => {
    if (booking.unloading_status !== "delivery_ready") return;
    if (!booking.delivery_info?.id) {
      message.error("No delivery address found");
      return;
    }

    // Navigate to admin booking details for shipment creation
    message.success("Delivery info ready! Opening shipment creation...");

    // For now, just show a success message that the booking is ready for shipment
    // The actual shipment creation UI is already implemented in BookingsAdmin
    message.info("Go to BookingsAdmin > Delivery tab to create shipments");
  };

  // Mark booking as completed when shipment is successfully created
  const markBookingAsCompleted = async (bookingId: number) => {
    try {
      const { error } = await supabase
        .from("bookings")
        .update({
          unloading_status: "completed",
          unloaded_at: new Date().toISOString(),
        })
        .eq("id", bookingId);

      if (error) throw error;

      // Update local state
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === bookingId
            ? { ...booking, unloading_status: "completed" as const }
            : booking
        )
      );

      message.success(
        "Booking marked as completed! Shipment created successfully. You can now download shipping labels."
      );

      // Keep the modal open so users can download shipping labels
      // setDeliveryModalVisible(false); // Commented out to keep modal open

      // Refresh data to get latest state
      await fetchContainerData();
    } catch (error) {
      console.error("Error marking booking as completed:", error);
      message.error("Failed to mark booking as completed");
    }
  };

  // Pallet label generation
  const generatePalletLabels = async (booking: BookingInfo) => {
    if (!booking.total_actual_pallets || booking.total_actual_pallets === 0) {
      message.info("No pallets to generate labels for (0 pallets)");
      return;
    }

    try {
      setUpdating(`pallet-labels-${booking.id}`);

      const palletCount = booking.total_actual_pallets;
      const totalCartons = booking.total_actual_cartons || 0;
      const cartonsPerPallet = Math.floor(totalCartons / palletCount);
      const partialPalletCartons = totalCartons % palletCount;

      // Compact single-page PDF styles
      const styles = StyleSheet.create({
        page: {
          padding: 15,
          fontFamily: "Helvetica",
          backgroundColor: "#ffffff",
          flexDirection: "column",
          height: "100%",
        },
        headerSection: {
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          paddingBottom: 8,
          borderBottom: "2px solid #1890ff",
        },
        logo: {
          fontSize: 16,
          fontWeight: "bold",
          color: "#1890ff",
        },
        title: {
          fontSize: 20,
          fontWeight: "bold",
          color: "#333",
        },
        palletId: {
          fontSize: 24,
          fontWeight: "bold",
          color: "#52c41a",
          textAlign: "center",
          marginBottom: 12,
          padding: 8,
          backgroundColor: "#f6ffed",
          border: "2px solid #52c41a",
          borderRadius: 4,
        },
        mainContent: {
          flex: 1,
          justifyContent: "space-between",
        },
        infoGrid: {
          flexDirection: "row",
          flexWrap: "wrap",
          marginBottom: 10,
        },
        infoBox: {
          width: "32%",
          marginBottom: 8,
          marginRight: "1.5%",
          padding: 6,
          backgroundColor: "#fafafa",
          border: "1px solid #d9d9d9",
          borderRadius: 2,
        },
        label: {
          fontSize: 8,
          fontWeight: "bold",
          color: "#666",
          marginBottom: 2,
          textTransform: "uppercase",
        },
        value: {
          fontSize: 12,
          fontWeight: "bold",
          color: "#333",
        },
        largeValue: {
          fontSize: 14,
          fontWeight: "bold",
          color: "#1890ff",
        },
        productsList: {
          marginBottom: 10,
          padding: 8,
          backgroundColor: "#f0f5ff",
          border: "1px solid #91d5ff",
          borderRadius: 2,
        },
        productsTitle: {
          fontSize: 10,
          fontWeight: "bold",
          color: "#1890ff",
          marginBottom: 4,
        },
        productItem: {
          fontSize: 9,
          color: "#333",
          marginBottom: 2,
          paddingLeft: 4,
        },
        notesSection: {
          marginBottom: 10,
          padding: 8,
          backgroundColor: "#fff7e6",
          border: "1px solid #ffd591",
          borderRadius: 2,
        },
        notesTitle: {
          fontSize: 9,
          fontWeight: "bold",
          color: "#fa8c16",
          marginBottom: 4,
        },
        notesText: {
          fontSize: 8,
          color: "#333",
          lineHeight: 1.2,
        },
        scanSection: {
          textAlign: "center",
          padding: 8,
          backgroundColor: "#f0f0f0",
          borderRadius: 2,
        },
        scanText: {
          fontSize: 10,
          fontWeight: "bold",
          color: "#333",
          fontFamily: "Courier",
        },
        footer: {
          marginTop: 8,
          textAlign: "center",
          fontSize: 7,
          color: "#999",
          borderTop: "1px solid #e8e8e8",
          paddingTop: 4,
        },
        warningBox: {
          padding: 6,
          backgroundColor: "#fff2e8",
          border: "1px solid #ff7a45",
          borderRadius: 2,
          marginBottom: 8,
        },
        warningText: {
          fontSize: 9,
          fontWeight: "bold",
          color: "#ff7a45",
          textAlign: "center",
        },
      });

      // Calculate product distribution logic
      const calculateProductDistribution = (palletNumber: number) => {
        const distributedProducts = [];

        for (const product of booking.products) {
          if (!product.actual_cartons) continue;

          const productCartonsOnThisPallet = Math.ceil(
            (product.actual_cartons / totalCartons) *
              (palletNumber === palletCount && partialPalletCartons > 0
                ? partialPalletCartons
                : cartonsPerPallet)
          );

          if (productCartonsOnThisPallet > 0) {
            distributedProducts.push({
              ...product,
              cartonsOnPallet: Math.min(
                productCartonsOnThisPallet,
                product.actual_cartons
              ),
            });
          }
        }

        return distributedProducts;
      };

      // Generate labels for each pallet
      for (let palletNumber = 1; palletNumber <= palletCount; palletNumber++) {
        const isPartialPallet =
          palletNumber === palletCount && partialPalletCartons > 0;
        const palletCartons = isPartialPallet
          ? partialPalletCartons
          : cartonsPerPallet;
        const productsOnPallet = calculateProductDistribution(palletNumber);
        const palletId = `${booking.booking_reference}-P${palletNumber
          .toString()
          .padStart(2, "0")}`;

        const PalletLabelDocument = (
          <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
              {/* Header */}
              <View style={styles.headerSection}>
                <PDFText style={styles.logo}>E-FREIGHT</PDFText>
                <PDFText style={styles.title}>PALLET LABEL</PDFText>
                <PDFText style={styles.logo}>
                  {new Date().getFullYear()}
                </PDFText>
              </View>

              <View style={styles.mainContent}>
                {/* Pallet ID - prominently displayed */}
                <View style={styles.palletId}>
                  <PDFText>{palletId}</PDFText>
                </View>

                {/* Warning for partial pallet */}
                {isPartialPallet && (
                  <View style={styles.warningBox}>
                    <PDFText style={styles.warningText}>
                      ⚠️ PARTIAL PALLET - HANDLE WITH CARE
                    </PDFText>
                  </View>
                )}

                {/* Information Grid */}
                <View style={styles.infoGrid}>
                  <View style={styles.infoBox}>
                    <PDFText style={styles.label}>Booking Reference</PDFText>
                    <PDFText style={styles.largeValue}>
                      {booking.booking_reference}
                    </PDFText>
                  </View>

                  <View style={styles.infoBox}>
                    <PDFText style={styles.label}>Company</PDFText>
                    <PDFText style={styles.value}>
                      {booking.company_name}
                    </PDFText>
                  </View>

                  <View style={styles.infoBox}>
                    <PDFText style={styles.label}>Pallet Number</PDFText>
                    <PDFText style={styles.largeValue}>
                      {palletNumber} of {palletCount}
                    </PDFText>
                  </View>

                  <View style={styles.infoBox}>
                    <PDFText style={styles.label}>Cartons on Pallet</PDFText>
                    <PDFText style={styles.largeValue}>{palletCartons}</PDFText>
                  </View>

                  <View style={styles.infoBox}>
                    <PDFText style={styles.label}>
                      Total Booking Cartons
                    </PDFText>
                    <PDFText style={styles.value}>{totalCartons}</PDFText>
                  </View>

                  <View style={styles.infoBox}>
                    <PDFText style={styles.label}>Generated</PDFText>
                    <PDFText style={styles.value}>
                      {new Date().toLocaleDateString()}
                    </PDFText>
                  </View>
                </View>

                {/* Products List */}
                <View style={styles.productsList}>
                  <PDFText style={styles.productsTitle}>
                    📦 Products on this Pallet:
                  </PDFText>
                  {productsOnPallet.map((product) => (
                    <PDFText key={product.id} style={styles.productItem}>
                      • {product.name} - {product.cartonsOnPallet} carton(s)
                      {product.ean && ` (EAN: ${product.ean})`}
                    </PDFText>
                  ))}
                </View>

                {/* Palletizing Notes */}
                {booking.palletizing_notes && (
                  <View style={styles.notesSection}>
                    <PDFText style={styles.notesTitle}>
                      📝 Palletizing Notes:
                    </PDFText>
                    <PDFText style={styles.notesText}>
                      {booking.palletizing_notes}
                    </PDFText>
                  </View>
                )}

                {/* Scan Section */}
                <View style={styles.scanSection}>
                  <PDFText style={styles.scanText}>{palletId}</PDFText>
                </View>
              </View>

              {/* Footer */}
              <PDFText style={styles.footer}>
                E-Freight Forwarding | Container Unloading System | Generated:{" "}
                {new Date().toLocaleDateString()}
              </PDFText>
            </Page>
          </Document>
        );

        // Generate and download the PDF
        const blob = await pdf(PalletLabelDocument).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${palletId.replace("#", "_")}_Label.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Small delay between downloads to prevent browser issues
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      message.success(`Generated ${palletCount} pallet label(s) successfully!`);
    } catch (error) {
      console.error("Error generating pallet labels:", error);
      message.error("Failed to generate pallet labels");
    } finally {
      setUpdating(null);
    }
  };

  const resetBookingToStep = async (
    bookingId: number,
    step: "counting" | "delivery_ready"
  ) => {
    try {
      setUpdating(`reset-${bookingId}`);

      const updates: any = {
        unloading_status: step === "counting" ? null : step,
      };

      // Reset data based on step
      if (step === "counting") {
        // Reset all product counts for this booking
        const booking = bookings.find((b) => b.id === bookingId);
        if (booking) {
          for (const product of booking.products) {
            await supabase
              .from("booking_product")
              .update({ actual_cartons: null })
              .eq("id", product.id);
          }
        }
        updates.total_actual_cartons = null;
        updates.total_actual_pallets = null;
        updates.unloading_notes = null;
      }

      // Update booking
      const { error } = await supabase
        .from("bookings")
        .update(updates)
        .eq("id", bookingId);

      if (error) throw error;

      message.success(`Booking reset to ${step} step`);

      // Update local state instead of full page refresh to preserve tab state
      setBookings((prevBookings) =>
        prevBookings.map((booking) => {
          if (booking.id !== bookingId) return booking;

          let newStatus: BookingInfo["unloading_status"];

          if (step === "counting") {
            // Check if any products are completed for this booking
            const hasCompletedProducts = booking.products.some(
              (p) => p.completed
            );
            newStatus = hasCompletedProducts ? "counting" : "pending";
          } else {
            newStatus = step;
          }

          return {
            ...booking,
            unloading_status: newStatus,
            total_actual_cartons:
              step === "counting" ? null : booking.total_actual_cartons,
            total_actual_pallets:
              step === "counting" ? null : booking.total_actual_pallets,
            palletizing_notes:
              step === "counting" ? null : booking.palletizing_notes,
            // Reset product counts if going back to counting
            products:
              step === "counting"
                ? booking.products.map((p) => ({
                    ...p,
                    actual_cartons: null,
                    completed: false,
                  }))
                : booking.products,
          };
        })
      );
    } catch (error) {
      console.error("Error resetting booking:", error);
      message.error("Failed to reset booking");
    } finally {
      setUpdating(null);
    }
  };

  // handlePalletization and savePalletInfo functions removed - palletizing step is skipped

  const handleDeliveryCreation = (booking: BookingInfo) => {
    if (booking.unloading_status !== "delivery_ready") return;

    setSelectedBooking(booking);
    setDeliveryModalVisible(true);
  };

  const getBookingStepStatus = (booking: BookingInfo): number => {
    switch (booking.unloading_status) {
      case "pending":
        return 0;
      case "counting":
        return 0;
      case "delivery_ready":
        return 1;
      case "completed":
        return booking.has_delivery_address ? 2 : 1;
      default:
        return 0;
    }
  };

  const getStatusColor = (status: BookingInfo["unloading_status"]): string => {
    switch (status) {
      case "pending":
        return "default";
      case "counting":
        return "processing";
      case "delivery_ready":
        return "success";
      case "completed":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusText = (booking: BookingInfo): string => {
    if (booking.unloading_status === "completed") {
      return booking.has_delivery_address ? "Ready for Shipment" : "In Storage";
    }

    switch (booking.unloading_status) {
      case "pending":
        return "Not Started";
      case "counting":
        return "Counting Products";
      case "delivery_ready":
        return "Ready for Delivery Setup";
      default:
        return "Unknown";
    }
  };

  // Calculate overall progress
  const totalBookings = bookings.length;
  const completedBookings = bookings.filter(
    (b) => b.unloading_status === "completed"
  ).length;
  const progressPercent =
    totalBookings > 0 ? (completedBookings / totalBookings) * 100 : 0;

  // Define these variables early so they can be used in useEffect dependencies
  const isUnloadingInProgress = container?.unloading_status === "in_progress";
  const isUnloadingCompleted = container?.unloading_status === "completed";
  const canStartUnloading = !isUnloadingInProgress && !isUnloadingCompleted;

  // Filter bookings based on search query
  const filterBookings = (bookingsToFilter: BookingInfo[]) => {
    if (!searchQuery.trim()) return bookingsToFilter;

    const query = searchQuery.toLowerCase();
    const filtered = bookingsToFilter.filter(
      (booking) =>
        // Search in booking reference
        booking.booking_reference.toLowerCase().includes(query) ||
        // Search in company name
        booking.company_name.toLowerCase().includes(query) ||
        // Search in products
        booking.products.some(
          (product) =>
            // Search in product name
            product.name.toLowerCase().includes(query) ||
            // Search in EAN
            (product.ean && product.ean.toString().includes(query))
        )
    );

    // Auto-expand bookings that have matching products when searching
    if (query.length > 2) {
      const bookingsWithMatchingProducts = filtered.filter((booking) =>
        booking.products.some(
          (product) =>
            product.name.toLowerCase().includes(query) ||
            (product.ean && product.ean.toString().includes(query))
        )
      );

      if (bookingsWithMatchingProducts.length > 0) {
        const expandIds = bookingsWithMatchingProducts.map((b) => b.id);
        setExpandedBookings((prev) => new Set([...prev, ...expandIds]));
      }
    }

    return filtered;
  };

  const getBookingsByStatus = () => {
    const allFilteredBookings = filterBookings(bookings);

    // Group bookings by delivery type (not completion status)
    const deliveryBookings = allFilteredBookings.filter(
      (b) => b.has_delivery_address
    );

    const storageBookings = allFilteredBookings.filter(
      (b) => !b.has_delivery_address
    );

    const grouped = {
      delivery: deliveryBookings,
      storage: storageBookings,
    };
    return grouped;
  };

  // Helper function to check if all products in a booking are counted
  const areAllProductsCounted = (booking: BookingInfo): boolean => {
    return booking.products.every((product) => product.completed);
  };

  // Helper functions for UI optimization
  const toggleBookingExpanded = (bookingId: number) => {
    const newExpanded = new Set(expandedBookings);
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId);
    } else {
      newExpanded.add(bookingId);
    }
    setExpandedBookings(newExpanded);
  };

  // Global keypress listener for barcode scanning
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      // Clear existing timeout
      if (barcodeTimeout.current) {
        clearTimeout(barcodeTimeout.current);
      }

      // Add character to buffer
      if (event.key.length === 1) {
        // Only single characters, not special keys
        barcodeBuffer.current += event.key;
        console.log(
          `📝 Adding character "${event.key}" to buffer. Current buffer: "${barcodeBuffer.current}" (length: ${barcodeBuffer.current.length})`
        );
      }

      // Wait for complete barcode transmission
      // Increased timeout to ensure full barcode capture (1000ms to be absolutely sure)
      barcodeTimeout.current = setTimeout(() => {
        const scannedCode = barcodeBuffer.current.trim();
        console.log(
          `📱 Zebra scanner buffer FINAL: "${scannedCode}" (length: ${scannedCode.length})`
        );

        // Accept EAN codes of various lengths (8, 12, 13, 14 digits)
        if (scannedCode.length >= 8 && /^\d+$/.test(scannedCode)) {
          // Check if this looks like a truncated EAN
          if (scannedCode.length === 8) {
            console.warn(
              `⚠️ Detected 8-digit code: "${scannedCode}" - This might be truncated. Check scanner settings for full EAN transmission.`
            );
          }
          console.log(
            `✅ Valid EAN detected from Zebra scanner: ${scannedCode} (${scannedCode.length} digits)`
          );
          searchProductByEAN(scannedCode, true); // Mark as from scanner for auto-increment logic
        } else if (scannedCode.length >= 3) {
          // Log shorter codes for debugging
          console.log(
            `⚠️ Short scan detected: "${scannedCode}" - might be partial or non-EAN`
          );
        } else if (scannedCode.length > 0) {
          console.log(
            `❌ Very short scan: "${scannedCode}" - likely noise or partial transmission`
          );
        }
        barcodeBuffer.current = ""; // Clear buffer
      }, 1000); // Increased timeout to 1000ms to ensure complete barcode capture
    };

    // Handle paste events for manual EAN entry
    const handlePaste = (event: ClipboardEvent) => {
      const pastedData = event.clipboardData?.getData("text");
      if (pastedData && pastedData.length >= 8) {
        event.preventDefault();
        console.log(`📋 Pasted EAN code: ${pastedData}`);
        searchProductByEAN(pastedData, false); // Not from scanner - just highlight/open
      }
    };

    // Add event listeners
    document.addEventListener("keypress", handleKeyPress);
    document.addEventListener("paste", handlePaste);

    // Cleanup
    return () => {
      document.removeEventListener("keypress", handleKeyPress);
      document.removeEventListener("paste", handlePaste);
      if (barcodeTimeout.current) {
        clearTimeout(barcodeTimeout.current);
      }
    };
  }, [bookings, autoScanEnabled, isUnloadingInProgress]); // Dependencies for the effect

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyPress = (event: KeyboardEvent) => {
      // Only handle if not in an input field or modal
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement ||
        deliveryModalVisible ||
        containerResetModalVisible
      ) {
        return;
      }

      // IMPORTANT: Don't interfere with barcode scanning
      // If we're currently accumulating characters in the buffer, ignore keyboard shortcuts
      if (barcodeBuffer.current.length > 0) {
        console.log(
          `🚫 Ignoring keyboard shortcut "${event.key}" - barcode scanning in progress`
        );
        return;
      }

      const key = event.key.toLowerCase();

      // Show shortcuts modal (Cmd/Ctrl + ?)
      if (key === "?" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setShortcutsModalVisible(true);
        return;
      }

      // Tab navigation (1-2)
      if (!event.ctrlKey && !event.altKey && !event.metaKey) {
        if (key === "1") {
          event.preventDefault();
          setActiveTab("delivery");
          return;
        }
        if (key === "2") {
          event.preventDefault();
          setActiveTab("storage");
          return;
        }

        // Toggle auto-scan mode
        if (key === "s") {
          event.preventDefault();
          setAutoScanEnabled((prev) => !prev);
          message.info(
            `Zebra Scanner Mode ${!autoScanEnabled ? "enabled" : "disabled"}`
          );
          return;
        }

        // Search products
        if (key === "f") {
          event.preventDefault();
          const searchInput = document.querySelector(
            'input[placeholder*="Search"]'
          ) as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
          return;
        }

        // Reset container (R)
        if (key === "r") {
          event.preventDefault();
          setContainerResetModalVisible(true);
          return;
        }

        // Expand/Collapse all bookings (E)
        if (key === "e") {
          event.preventDefault();
          const visibleBookings = filterBookings(bookings);
          if (expandedBookings.size === visibleBookings.length) {
            // All expanded, collapse all
            setExpandedBookings(new Set());
            message.info("Collapsed all bookings");
          } else {
            // Expand all
            setExpandedBookings(new Set(visibleBookings.map((b) => b.id)));
            message.info("Expanded all bookings");
          }
          return;
        }
      }

      // Refresh data (Ctrl+R or Cmd+R)
      if (key === "r" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        fetchContainerData();
        message.info("Refreshing container data...");
        return;
      }
    };

    document.addEventListener("keydown", handleGlobalKeyPress);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyPress);
    };
  }, [
    autoScanEnabled,

    deliveryModalVisible,
    containerResetModalVisible,
    fetchContainerData,
  ]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!container) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Empty description="Container not found" />
        <Button
          onClick={() => navigate(`/admin/containers/${containerId}`)}
          style={{ marginTop: 16 }}
        >
          Back to Container Details
        </Button>
      </div>
    );
  }

  return (
    <div
      className="container-unloading-page"
      style={{ padding: "12px 24px", maxWidth: "100vw", margin: "0" }}
    >
      {/* Auto Scan Banner */}
      {isUnloadingInProgress && autoScanEnabled && (
        <div
          className="mobile-scan-banner"
          style={{
            backgroundColor: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: "6px",
            padding: "8px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#389e0d",
            fontWeight: "500",
          }}
        >
          <ScanOutlined style={{ marginRight: "8px" }} />
          🔧 Zebra Scanner Mode: ON - Scan EAN codes to automatically +1 carton
          count
        </div>
      )}

      {/* Mobile Search Bar */}
      <div className="mobile-search" style={{ display: "none" }}>
        <Input
          placeholder="🔍 Search EAN or product name..."
          value={searchQuery}
          onChange={(e) => {
            const value = e.target.value;
            setSearchQuery(value);

            // Trigger search for EAN-like values in search input (just highlight, don't increment)
            if (value.length >= 8 && /^\d+$/.test(value)) {
              console.log(
                `🔍 Search input detected EAN-like value: ${value}, highlighting product`
              );
              // Use false for fromScanner - search input should only highlight/open products
              searchProductByEAN(value, false);
            }
          }}
          size="large"
          allowClear
          prefix={<ScanOutlined />}
        />
      </div>

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <Button
          onClick={() => navigate(`/admin/containers/${containerId}`)}
          icon={<ArrowLeftOutlined />}
          style={{ marginBottom: 12 }}
          size="small"
        >
          Back to Container Details
        </Button>

        <Card size="small">
          <Row justify="space-between" align="middle">
            <Col flex="auto">
              <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
                📦 Container {container.reference} - Unloading Workflow
              </Title>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {completedBookings}/{totalBookings} bookings completed
                {container.unloading_started_at && (
                  <>
                    {" "}
                    • Started{" "}
                    {dayjs(container.unloading_started_at).format(
                      "DD/MM HH:mm"
                    )}
                  </>
                )}
              </Text>
            </Col>
            <Col className="desktop-only">
              <Space size="small">
                <Input
                  placeholder="Search EAN or product name..."
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);

                    // Trigger search for EAN-like values in search input (just highlight, don't increment)
                    if (value.length >= 8 && /^\d+$/.test(value)) {
                      console.log(
                        `🔍 Desktop search input detected EAN-like value: ${value}, highlighting product`
                      );
                      // Use false for fromScanner - search input should only highlight/open products
                      searchProductByEAN(value, false);
                    }
                  }}
                  style={{ width: 200 }}
                  size="small"
                  allowClear
                  prefix={<ScanOutlined />}
                />
              </Space>
            </Col>
            <Col>
              <Space size="small">
                <Tag
                  color={
                    isUnloadingCompleted
                      ? "green"
                      : isUnloadingInProgress
                      ? "orange"
                      : "default"
                  }
                  style={{ fontSize: "11px" }}
                >
                  {isUnloadingCompleted
                    ? "Completed"
                    : isUnloadingInProgress
                    ? "In Progress"
                    : "Not Started"}
                </Tag>
                {isUnloadingInProgress && (
                  <Space size="small">
                    <ScanOutlined style={{ fontSize: "12px" }} />
                    <Switch
                      checked={autoScanEnabled}
                      onChange={setAutoScanEnabled}
                      size="small"
                    />
                    <Text type="secondary" style={{ fontSize: "11px" }}>
                      Zebra Scanner
                    </Text>
                  </Space>
                )}
                {canStartUnloading && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    onClick={startUnloading}
                    loading={startingUnload}
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                    }}
                  >
                    Start Unloading
                  </Button>
                )}
                {isUnloadingInProgress && (
                  <Button
                    danger
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => setContainerResetModalVisible(true)}
                    loading={updating === "container-reset"}
                  >
                    Reset Container
                  </Button>
                )}
                <Button
                  size="small"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setShortcutsModalVisible(true)}
                  title="Keyboard Shortcuts (Ctrl+? or ⌘+?)"
                >
                  Help
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Progress Bar */}
          {isUnloadingInProgress && (
            <div style={{ marginTop: 12 }}>
              <Progress
                percent={Math.round(progressPercent)}
                status={progressPercent === 100 ? "success" : "active"}
                size="small"
                format={() => `${completedBookings}/${totalBookings} completed`}
              />
            </div>
          )}
        </Card>
      </div>

      {/* Workflow Steps Info - Updated to skip palletize */}
      {isUnloadingInProgress && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Steps size="small" style={{ margin: "8px 0" }}>
            <Step title="Count" icon={<CheckCircleOutlined />} />
            <Step title="Delivery" icon={<TruckOutlined />} />
            <Step title="Ship" icon={<EnvironmentOutlined />} />
          </Steps>
        </Card>
      )}

      {/* Tabbed Bookings Interface */}
      {(() => {
        const groupedBookings = getBookingsByStatus();

        const renderCompactBookingCard = (booking: BookingInfo) => {
          const currentStep = getBookingStepStatus(booking);
          const isExpanded = expandedBookings.has(booking.id);
          const completedProducts = booking.products.filter(
            (p) => p.completed
          ).length;
          const totalProducts = booking.products.length;

          // Calculate cartons scanned vs estimated
          const totalScannedCartons = booking.products.reduce(
            (sum, p) => sum + (p.actual_cartons || 0),
            0
          );
          const totalEstimatedCartons = booking.products.reduce(
            (sum, p) => sum + p.estimated_cartons,
            0
          );

          // Get EAN list for header display (full EANs)
          const eanList = booking.products
            .map((p) => p.ean)
            .filter((ean) => ean != null)
            .map((ean) => String(ean))
            .join(", ");

          return (
            <Card
              key={booking.id}
              className="mobile-booking-card"
              size="small"
              style={{
                marginBottom: 8,
                border:
                  booking.unloading_status === "completed"
                    ? "2px solid #52c41a"
                    : undefined,
              }}
            >
              {/* Compact Header - Always Visible */}
              <Row
                className="mobile-booking-header"
                justify="space-between"
                align="middle"
                style={{ cursor: "pointer" }}
                onClick={() => toggleBookingExpanded(booking.id)}
              >
                <Col flex="auto">
                  <Space
                    size="small"
                    direction="vertical"
                    style={{ width: "100%" }}
                  >
                    <Space size="small">
                      <Text strong style={{ fontSize: 14 }}>
                        {booking.booking_reference}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {booking.company_name}
                      </Text>
                      <Badge
                        count={`${completedProducts}/${totalProducts}`}
                        style={{
                          backgroundColor:
                            completedProducts === totalProducts
                              ? "#52c41a"
                              : "#1890ff",
                          fontSize: "10px",
                        }}
                      />
                      <Badge
                        count={`${totalScannedCartons}/${totalEstimatedCartons}`}
                        style={{
                          backgroundColor: "#722ed1",
                          fontSize: "10px",
                        }}
                        title="Cartons scanned vs estimated"
                      />
                      <Tag
                        color={getStatusColor(booking.unloading_status)}
                        style={{ fontSize: "10px" }}
                      >
                        {getStatusText(booking)}
                      </Tag>
                      {booking.has_delivery_address ? (
                        <Tag color="blue" style={{ fontSize: "9px" }}>
                          🚚 Delivery
                        </Tag>
                      ) : (
                        <Tag color="orange" style={{ fontSize: "9px" }}>
                          📦 Storage
                        </Tag>
                      )}
                    </Space>
                    {eanList && (
                      <Text
                        type="secondary"
                        style={{
                          fontSize: "10px",
                          fontFamily: "monospace",
                          marginTop: "2px",
                        }}
                      >
                        EANs: {eanList}
                      </Text>
                    )}
                  </Space>
                </Col>
                <Col>
                  <Space size="small">
                    {/* Quick action buttons */}
                    {booking.unloading_status === "delivery_ready" &&
                      booking.has_delivery_address && (
                        <Button
                          size="small"
                          type="primary"
                          icon={<TruckOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeliveryCreation(booking);
                          }}
                          style={{
                            backgroundColor: "#722ed1",
                            borderColor: "#722ed1",
                          }}
                        >
                          Ship
                        </Button>
                      )}
                    {booking.unloading_status === "delivery_ready" &&
                      !booking.has_delivery_address && (
                        <Button
                          size="small"
                          type="primary"
                          icon={<InboxOutlined />}
                          onClick={async (e) => {
                            e.stopPropagation();
                            // Mark as completed for storage
                            const { error } = await supabase
                              .from("bookings")
                              .update({ unloading_status: "completed" })
                              .eq("id", booking.id);

                            if (error) {
                              message.error("Failed to mark as completed");
                            } else {
                              message.success("Booking marked as in storage");
                              fetchContainerData();
                            }
                          }}
                          style={{
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                          }}
                        >
                          Storage
                        </Button>
                      )}
                    {booking.unloading_status === "completed" && (
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        disabled
                        onClick={(e) => e.stopPropagation()}
                      >
                        {booking.has_delivery_address ? "Shipped" : "Stored"}
                      </Button>
                    )}

                    {/* Reset dropdown - only show if booking has progressed beyond pending */}
                    {(booking.unloading_status === "delivery_ready" ||
                      booking.unloading_status === "completed") && (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "reset-counting",
                              label: "Reset to Counting",
                              icon: <ReloadOutlined />,
                              onClick: () =>
                                resetBookingToStep(booking.id, "counting"),
                            },
                          ] as MenuProps["items"],
                        }}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <Button
                          size="small"
                          icon={<MoreOutlined />}
                          loading={updating === `reset-${booking.id}`}
                          onClick={(e) => e.stopPropagation()}
                          title="Reset options"
                        />
                      </Dropdown>
                    )}

                    <Button
                      size="small"
                      type="text"
                      style={{ padding: "0 4px", fontSize: "12px" }}
                    >
                      {isExpanded ? "🔽" : "▶️"}
                    </Button>
                  </Space>
                </Col>
              </Row>

              {/* Expandable Content - Only show when expanded */}
              {isExpanded && (
                <div
                  className="mobile-booking-content"
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  {/* Progress Steps - Updated to skip palletize */}
                  <Steps
                    current={currentStep}
                    size="small"
                    style={{ marginBottom: 12 }}
                    status={
                      booking.unloading_status === "completed"
                        ? "finish"
                        : "process"
                    }
                  >
                    <Step title="Count" />
                    {booking.has_delivery_address ? (
                      <>
                        <Step title="Delivery" />
                        <Step title="Ship" />
                      </>
                    ) : (
                      <Step title="Storage" />
                    )}
                  </Steps>

                  {/* Products Grid */}
                  <div style={{ marginBottom: 12 }}>
                    <Text
                      strong
                      style={{
                        fontSize: 12,
                        marginBottom: 8,
                        display: "block",
                      }}
                    >
                      Products ({booking.products.length})
                    </Text>
                    <Row gutter={[6, 6]}>
                      {booking.products.map((product) => (
                        <Col
                          xs={12}
                          sm={8}
                          md={6}
                          lg={4}
                          xl={3}
                          key={product.id}
                        >
                          <Card
                            className={`mobile-product-row ${
                              highlightedProductId === product.id
                                ? "highlighted"
                                : ""
                            }`}
                            size="small"
                            ref={(el) => {
                              productRefs.current[product.id] = el;
                            }}
                            style={{
                              backgroundColor:
                                highlightedProductId === product.id
                                  ? "#fff7e6"
                                  : product.completed
                                  ? "#f6ffed"
                                  : "#fafafa",
                              border:
                                highlightedProductId === product.id
                                  ? "2px solid #fa8c16"
                                  : product.completed
                                  ? "1px solid #b7eb8f"
                                  : "1px solid #d9d9d9",
                              minHeight: "180px",
                              position: "relative",
                            }}
                          >
                            {/* Prominent delivery/storage status indicator - only show when highlighted and not all products counted */}
                            {highlightedProductId === product.id &&
                              booking.unloading_status !== "completed" &&
                              !areAllProductsCounted(booking) && (
                                <div
                                  className={`mobile-delivery-status ${
                                    booking.has_delivery_address
                                      ? "delivery"
                                      : "storage"
                                  }`}
                                >
                                  {booking.has_delivery_address
                                    ? "🚚 DELIVERY"
                                    : "📦 STORAGE"}
                                </div>
                              )}
                            <div style={{ textAlign: "center" }}>
                              <Text
                                strong
                                className="mobile-product-name"
                                style={{
                                  fontSize: "12px",
                                  display: "block",
                                  marginBottom: "4px",
                                }}
                              >
                                {product.name}
                              </Text>
                              {product.ean && (
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: "12px",
                                    display: "block",
                                    marginBottom: "6px",
                                    fontWeight: "bold",
                                    fontFamily: "monospace",
                                    color: "#1890ff",
                                  }}
                                >
                                  {String(product.ean)}
                                </Text>
                              )}

                              {/* Product specifications */}
                              <div
                                style={{
                                  backgroundColor: "#f9f9f9",
                                  padding: "4px 6px",
                                  borderRadius: "4px",
                                  marginBottom: "6px",
                                  fontSize: "10px",
                                }}
                              >
                                <Row gutter={[2, 2]}>
                                  <Col span={12}>
                                    <Text type="secondary">
                                      CBM: {product.cbm?.toFixed(3) || "-"}
                                    </Text>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary">
                                      {product.gross_weight || "-"}kg
                                    </Text>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary">
                                      Pcs: {product.pieces || "-"}
                                    </Text>
                                  </Col>
                                  <Col span={12}>
                                    <Text type="secondary">
                                      Est: {product.estimated_cartons}
                                    </Text>
                                  </Col>
                                </Row>
                              </div>

                              {/* Interactive carton counter */}
                              <div
                                className="mobile-product-count"
                                style={{
                                  backgroundColor: "#f0f0f0",
                                  padding: "2px",
                                  borderRadius: "8px",
                                  marginBottom: "6px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  minHeight: "44px",
                                }}
                              >
                                {isUnloadingInProgress && !product.completed ? (
                                  <>
                                    <Button
                                      type="text"
                                      icon={<MinusOutlined />}
                                      onClick={() => {
                                        hapticFeedback();
                                        quickAddCartons(product.id, -1);
                                      }}
                                      disabled={
                                        updating === `product-${product.id}`
                                      }
                                      style={{
                                        minWidth: "36px",
                                        height: "36px",
                                        borderRadius: "6px",
                                        backgroundColor: "#ff4d4f",
                                        color: "white",
                                        border: "none",
                                      }}
                                    />
                                    <div
                                      style={{ textAlign: "center", flex: 1 }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: "20px",
                                          fontWeight: "bold",
                                          color: "#1890ff",
                                          display: "block",
                                        }}
                                      >
                                        {product.actual_cartons || 0} /{" "}
                                        {product.estimated_cartons}
                                      </Text>
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: "9px" }}
                                      >
                                        cartons
                                      </Text>
                                    </div>
                                    <Button
                                      type="text"
                                      icon={<PlusOutlined />}
                                      onClick={() => {
                                        hapticFeedback();
                                        quickAddCartons(product.id, 1);
                                      }}
                                      disabled={
                                        updating === `product-${product.id}`
                                      }
                                      style={{
                                        minWidth: "36px",
                                        height: "36px",
                                        borderRadius: "6px",
                                        backgroundColor: "#52c41a",
                                        color: "white",
                                        border: "none",
                                      }}
                                    />
                                  </>
                                ) : (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      width: "100%",
                                      padding: "8px 0",
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontSize: "20px",
                                        fontWeight: "bold",
                                        color: product.completed
                                          ? "#52c41a"
                                          : "#1890ff",
                                        display: "block",
                                      }}
                                    >
                                      {product.actual_cartons || 0} /{" "}
                                      {product.estimated_cartons}
                                    </Text>
                                    <Text
                                      type="secondary"
                                      style={{ fontSize: "9px" }}
                                    >
                                      cartons
                                    </Text>
                                  </div>
                                )}
                              </div>

                              {/* Interactive pallet counter for storage bookings */}
                              {!booking.has_delivery_address && (
                                <div
                                  style={{
                                    backgroundColor: "#f9f0ff",
                                    padding: "2px",
                                    borderRadius: "8px",
                                    marginBottom: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    minHeight: "44px",
                                  }}
                                >
                                  {isUnloadingInProgress &&
                                  !product.completed ? (
                                    <>
                                      <Button
                                        type="text"
                                        icon={<MinusOutlined />}
                                        onClick={() => {
                                          hapticFeedback();
                                          quickAddProductPallets(
                                            product.id,
                                            -1
                                          );
                                        }}
                                        disabled={
                                          updating ===
                                          `product-pallets-${product.id}`
                                        }
                                        style={{
                                          minWidth: "36px",
                                          height: "36px",
                                          borderRadius: "6px",
                                          backgroundColor: "#ff4d4f",
                                          color: "white",
                                          border: "none",
                                        }}
                                      />
                                      <div
                                        style={{ textAlign: "center", flex: 1 }}
                                      >
                                        <Text
                                          style={{
                                            fontSize: "16px",
                                            fontWeight: "bold",
                                            color: "#722ed1",
                                            display: "block",
                                          }}
                                        >
                                          {product.pallets || 0}
                                        </Text>
                                        <Text
                                          type="secondary"
                                          style={{ fontSize: "9px" }}
                                        >
                                          pallets
                                        </Text>
                                      </div>
                                      <Button
                                        type="text"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                          hapticFeedback();
                                          quickAddProductPallets(product.id, 1);
                                        }}
                                        disabled={
                                          updating ===
                                          `product-pallets-${product.id}`
                                        }
                                        style={{
                                          minWidth: "36px",
                                          height: "36px",
                                          borderRadius: "6px",
                                          backgroundColor: "#52c41a",
                                          color: "white",
                                          border: "none",
                                        }}
                                      />
                                    </>
                                  ) : (
                                    <div
                                      style={{
                                        textAlign: "center",
                                        width: "100%",
                                        padding: "8px 0",
                                      }}
                                    >
                                      <Text
                                        style={{
                                          fontSize: "16px",
                                          fontWeight: "bold",
                                          color: "#722ed1",
                                          display: "block",
                                        }}
                                      >
                                        {product.pallets || 0}
                                      </Text>
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: "9px" }}
                                      >
                                        pallets
                                      </Text>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Action buttons for incomplete products */}
                              {isUnloadingInProgress && !product.completed && (
                                <div
                                  style={{
                                    marginTop: 6,
                                    display: "flex",
                                    gap: "4px",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Button
                                    size="small"
                                    type="dashed"
                                    onClick={() =>
                                      setAllBoxesCounted(product.id)
                                    }
                                    disabled={
                                      updating === `product-${product.id}`
                                    }
                                    style={{
                                      fontSize: "9px",
                                      height: "24px",
                                      border: "1px dashed #52c41a",
                                      color: "#52c41a",
                                    }}
                                  >
                                    All
                                  </Button>
                                  <Button
                                    size="small"
                                    type="text"
                                    icon={<ReloadOutlined />}
                                    onClick={() =>
                                      resetProductCount(product.id)
                                    }
                                    disabled={
                                      updating === `product-${product.id}`
                                    }
                                    style={{
                                      fontSize: "9px",
                                      height: "24px",
                                      color: "#ff4d4f",
                                      padding: "0 6px",
                                    }}
                                  />
                                </div>
                              )}

                              {product.completed && (
                                <div style={{ marginTop: 6 }}>
                                  <Text
                                    strong
                                    style={{
                                      color: "#52c41a",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ✓ {product.actual_cartons}
                                  </Text>
                                </div>
                              )}
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>

                  {/* Booking-level pallet controls for delivery bookings */}
                  {booking.has_delivery_address && isUnloadingInProgress && (
                    <div style={{ marginBottom: 12, textAlign: "center" }}>
                      <div
                        style={{
                          backgroundColor: "#f0f8ff",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: "#1890ff",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          🚚 Delivery Pallets
                        </Text>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <Button
                            className="mobile-counter-btn"
                            type="primary"
                            danger
                            icon={<MinusOutlined />}
                            onClick={() => {
                              hapticFeedback();
                              quickAddBookingPallets(booking.id, -1);
                            }}
                            disabled={
                              updating === `booking-pallets-${booking.id}`
                            }
                            style={{
                              backgroundColor: "#1890ff",
                              borderColor: "#1890ff",
                            }}
                          />
                          <div
                            className="mobile-counter-display"
                            style={{
                              backgroundColor: "#f0f8ff",
                              border: "2px solid #1890ff",
                              fontSize: "18px",
                            }}
                          >
                            {booking.total_actual_pallets || 0}
                          </div>
                          <Button
                            className="mobile-counter-btn"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              hapticFeedback();
                              quickAddBookingPallets(booking.id, 1);
                            }}
                            disabled={
                              updating === `booking-pallets-${booking.id}`
                            }
                            style={{
                              backgroundColor: "#1890ff",
                              borderColor: "#1890ff",
                            }}
                          />
                        </div>
                        <Text
                          type="secondary"
                          style={{ fontSize: "10px", marginTop: "4px" }}
                        >
                          Mixed products per pallet
                        </Text>
                      </div>
                    </div>
                  )}

                  {/* Finish Button - appears when all cartons are counted */}
                  {isBookingReadyToFinish(booking) &&
                    hasBookingPendingChanges(booking) && (
                      <div style={{ marginBottom: 12, textAlign: "center" }}>
                        <Button
                          type="primary"
                          size="large"
                          icon={<CheckCircleOutlined />}
                          onClick={() => finishBooking(booking)}
                          loading={finishingBookings.has(booking.id)}
                          style={{
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            height: "48px",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                        >
                          ✅ Finish Booking
                        </Button>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#52c41a",
                            marginTop: "4px",
                            fontWeight: "bold",
                          }}
                        >
                          Save all counts and complete booking
                        </div>
                      </div>
                    )}

                  {/* Generate Pallet Labels Button */}
                  {booking.total_actual_pallets &&
                    booking.total_actual_pallets > 0 &&
                    (booking.unloading_status === "delivery_ready" ||
                      booking.unloading_status === "completed") && (
                      <div style={{ marginBottom: 12, textAlign: "center" }}>
                        <Button
                          type="default"
                          icon={<InboxOutlined />}
                          onClick={() => generatePalletLabels(booking)}
                          loading={updating === `pallet-labels-${booking.id}`}
                          size="small"
                          style={{
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                            color: "white",
                          }}
                        >
                          🏷️ Generate Pallet Labels
                        </Button>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#8c8c8c",
                            marginTop: "4px",
                          }}
                        >
                          Print labels for {booking.total_actual_pallets}{" "}
                          pallet(s)
                        </div>
                      </div>
                    )}

                  {/* Summary info */}
                  {((booking.total_actual_cartons !== null &&
                    booking.total_actual_cartons !== undefined) ||
                    (booking.total_actual_pallets !== null &&
                      booking.total_actual_pallets !== undefined) ||
                    hasBookingPendingChanges(booking)) && (
                    <div
                      style={{
                        padding: "6px 8px",
                        backgroundColor: hasBookingPendingChanges(booking)
                          ? "#fff7e6"
                          : "#f0f0f0",
                        borderRadius: 4,
                        fontSize: "11px",
                        border: hasBookingPendingChanges(booking)
                          ? "1px solid #ffc53d"
                          : "none",
                      }}
                    >
                      <Space size="small">
                        {hasBookingPendingChanges(booking) && (
                          <Text
                            style={{ color: "#d46b08", fontWeight: "bold" }}
                          >
                            💾 Pending changes
                          </Text>
                        )}
                        {booking.total_actual_cartons !== null &&
                          booking.total_actual_cartons !== undefined && (
                            <Text type="secondary">
                              Total: {booking.total_actual_cartons} cartons
                            </Text>
                          )}
                        {booking.total_actual_pallets !== null &&
                          booking.total_actual_pallets !== undefined && (
                            <Text type="secondary">
                              Pallets: {booking.total_actual_pallets}
                            </Text>
                          )}
                        {booking.delivery_info && (
                          <Text type="secondary">
                            Status:{" "}
                            {booking.delivery_info.shipment_status?.toUpperCase()}
                          </Text>
                        )}
                      </Space>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        };

        return (
          <Tabs
            className="mobile-tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            size="large"
            items={[
              {
                key: "delivery",
                label: (
                  <Badge
                    count={groupedBookings.delivery.length}
                    size="small"
                    offset={[10, 0]}
                  >
                    🚚 Delivery
                  </Badge>
                ),
                children: (
                  <div>
                    {groupedBookings.delivery.map(renderCompactBookingCard)}
                    {groupedBookings.delivery.length === 0 && (
                      <Empty
                        description={
                          searchQuery
                            ? "No delivery bookings match your search"
                            : "No delivery bookings found"
                        }
                      />
                    )}
                  </div>
                ),
              },
              {
                key: "storage",
                label: (
                  <Badge
                    count={groupedBookings.storage.length}
                    size="small"
                    offset={[10, 0]}
                  >
                    📦 Storage
                  </Badge>
                ),
                children: (
                  <div>
                    {groupedBookings.storage.map(renderCompactBookingCard)}
                    {groupedBookings.storage.length === 0 && (
                      <Empty description="No storage bookings found" />
                    )}
                  </div>
                ),
              },
            ]}
          />
        );
      })()}

      {/* Pallet Modal removed - palletizing step is skipped */}

      {/* Create Shipment Modal */}
      <Modal
        className="mobile-modal"
        title={
          <Space>
            <TruckOutlined />
            Create Shipment - {selectedBooking?.booking_reference}
          </Space>
        }
        open={deliveryModalVisible}
        onCancel={() => {
          setDeliveryModalVisible(false);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setDeliveryModalVisible(false);
            }}
          >
            Close
          </Button>,
        ]}
        width="100%"
      >
        {/* Show info about the booking's cartons and pallets */}
        <Alert
          message={`Ready to ship ${
            selectedBooking?.total_actual_cartons || 0
          } cartons on ${selectedBooking?.total_actual_pallets || 0} pallets`}
          type="info"
          style={{ marginBottom: 16 }}
        />

        {selectedBooking?.delivery_info ? (
          <div>
            <Alert
              message="Choose Shipping Method"
              description="Select a shipping option and create Sendcloud shipment labels for this booking."
              type="info"
              style={{ marginBottom: 16 }}
            />

            <ShippingOptions
              deliveryInfo={{
                country: selectedBooking.delivery_info.country,
                postal_code: selectedBooking.delivery_info.postal_code,
                city: selectedBooking.delivery_info.city,
                street_name: selectedBooking.delivery_info.street_name,
                house_number: selectedBooking.delivery_info.house_number,
              }}
              bookingId={selectedBooking.id}
              deliveryCartons={
                selectedBooking.total_actual_cartons || undefined
              }
              deliveryPallets={
                selectedBooking.total_actual_pallets || undefined
              }
              onCreateShipment={(shippingMethodId) => {
                console.log("Create shipment with method:", shippingMethodId);
                setRefreshKey((prev) => prev + 1);
              }}
              onShipmentCreated={() => {
                setRefreshKey((prev) => prev + 1);
                // Mark booking as completed when shipment is created
                markBookingAsCompleted(selectedBooking.id);
              }}
            />

            <div style={{ marginTop: 24 }}>
              <SendcloudShipments
                key={refreshKey}
                bookingId={selectedBooking.id}
                onRefresh={() => {
                  setRefreshKey((prev) => prev + 1);
                }}
              />
            </div>
          </div>
        ) : (
          <Alert
            message="No Delivery Address"
            description="This booking doesn't have a delivery address set up. Please add delivery information first."
            type="warning"
          />
        )}
      </Modal>

      {/* Keyboard Shortcuts Modal */}
      <Modal
        className="mobile-modal"
        title={<Space>⌨️ Keyboard Shortcuts</Space>}
        open={shortcutsModalVisible}
        onCancel={() => setShortcutsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setShortcutsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width="100%"
      >
        <div style={{ fontSize: "14px" }}>
          <div style={{ marginBottom: "20px" }}>
            <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
              🗂️ Tab Navigation
            </Text>
            <div style={{ marginTop: "8px", marginLeft: "16px" }}>
              <div>
                <kbd>1</kbd> • 🚧 Not in Storage
              </div>
              <div>
                <kbd>2</kbd> • 🏪 In Storage
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <Text strong style={{ fontSize: "16px", color: "#52c41a" }}>
              ⚡ Quick Actions
            </Text>
            <div style={{ marginTop: "8px", marginLeft: "16px" }}>
              <div>
                <kbd>S</kbd> • Toggle Zebra Scanner Mode
              </div>
              <div>
                <kbd>F</kbd> • Focus Search Bar
              </div>
              <div>
                <kbd>E</kbd> • Expand/Collapse All Bookings
              </div>
              <div>
                <kbd>R</kbd> • Reset Container
              </div>
              <div>
                <kbd>Ctrl+R</kbd> / <kbd>⌘+R</kbd> • Refresh Data
              </div>
              <div>
                <kbd>Ctrl+?</kbd> / <kbd>⌘+?</kbd> • Show This Help
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <Text strong style={{ fontSize: "16px", color: "#faad14" }}>
              📝 Form Navigation
            </Text>
            <div style={{ marginTop: "8px", marginLeft: "16px" }}>
              <div>
                <kbd>Enter</kbd> • Move to Next Field
              </div>
              <div>
                <kbd>Enter</kbd> on Carton Input • Set to Estimated
              </div>
              <div>
                <kbd>Ctrl+Enter</kbd> in Text Areas • Save Form
              </div>
              <div>
                <kbd>Tab</kbd> / <kbd>Shift+Tab</kbd> • Navigate Fields
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <Text strong style={{ fontSize: "16px", color: "#722ed1" }}>
              📱 Zebra Scanner
            </Text>
            <div style={{ marginTop: "8px", marginLeft: "16px" }}>
              <div>📷 • Scan EAN Codes with Zebra mobile device</div>
              <div>
                ➕ • Auto-increment carton count by +1 (when Zebra Scanner Mode
                is ON)
              </div>
              <div>
                🔍 • Search bar typing only highlights products (no increment)
              </div>
              <div>⏰ • 50ms timeout optimized for Zebra scanners</div>
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#f6ffed",
              border: "1px solid #b7eb8f",
              borderRadius: "6px",
              padding: "12px",
              marginTop: "16px",
            }}
          >
            <Text strong style={{ color: "#389e0d" }}>
              💡 Pro Tips
            </Text>
            <div
              style={{ marginTop: "8px", fontSize: "13px", color: "#389e0d" }}
            >
              • Use number keys (1-2) to quickly switch between storage status
              tabs
              <br />
              • Enable Zebra Scanner Mode (S) for hands-free carton counting
              <br />
              • Enter key on carton inputs acts like "All" button
              <br />
              • Ctrl+Enter saves forms without using mouse
              <br />• Use virtual palletizing for goods staying per carton
            </div>
          </div>
        </div>
      </Modal>

      {/* Pallet Labels Download Modal */}
      <Modal
        className="mobile-modal"
        title={
          <Space>
            <InboxOutlined style={{ color: "#52c41a" }} />
            🏷️ Download Pallet Labels - {selectedBooking?.booking_reference}
          </Space>
        }
        open={palletLabelModalVisible}
        onCancel={() => setPalletLabelModalVisible(false)}
        footer={[
          <Button key="skip" onClick={() => setPalletLabelModalVisible(false)}>
            Skip for Now
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<InboxOutlined />}
            onClick={async () => {
              if (selectedBooking) {
                await generatePalletLabels(selectedBooking);
                setPalletLabelModalVisible(false);
              }
            }}
            loading={updating?.startsWith("pallet-labels-")}
            style={{
              backgroundColor: "#52c41a",
              borderColor: "#52c41a",
            }}
          >
            🏷️ Download Labels
          </Button>,
        ]}
        width="100%"
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: "16px", marginBottom: "16px" }}>
            ✅ Palletizing completed successfully!
          </div>

          {selectedBooking && (
            <Alert
              message={`Ready to generate ${selectedBooking.total_actual_pallets} pallet label(s)`}
              description={
                <div style={{ marginTop: "8px" }}>
                  <div>
                    📦 Total Cartons: {selectedBooking.total_actual_cartons}
                  </div>
                  <div>
                    🏷️ Total Pallets: {selectedBooking.total_actual_pallets}
                  </div>
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#8c8c8c",
                    }}
                  >
                    Labels will be downloaded as individual PDF files
                  </div>
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: "16px" }}
            />
          )}

          <div style={{ color: "#8c8c8c", fontSize: "14px" }}>
            You can also download these labels later from the booking card.
          </div>
        </div>
      </Modal>

      {/* Container Reset Confirmation Modal */}
      <Modal
        className="mobile-modal"
        title={
          <Space>
            <ReloadOutlined style={{ color: "#ff4d4f" }} />
            Reset Container Unloading
          </Space>
        }
        open={containerResetModalVisible}
        onCancel={() => setContainerResetModalVisible(false)}
        onOk={resetContainer}
        confirmLoading={updating === "container-reset"}
        okText="Yes, Reset Everything"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        width="100%"
      >
        <Alert
          message="⚠️ Complete Reset Warning"
          description={
            <div>
              <p>
                This will completely reset the container unloading process and:
              </p>
              <ul style={{ marginLeft: 20, marginTop: 8 }}>
                <li>🔄 Reset container status to "Not Started"</li>
                <li>📦 Clear ALL product carton counts</li>
                <li>📋 Remove all pallet information</li>
                <li>🚚 Delete delivery setup data</li>
                <li>⏰ Clear unloading timestamps</li>
              </ul>
              <p
                style={{ marginTop: 12, fontWeight: "bold", color: "#ff4d4f" }}
              >
                This action cannot be undone!
              </p>
            </div>
          }
          type="warning"
          showIcon
        />
      </Modal>
    </div>
  );
};

export default ContainerUnloadingPage;
